import {environment} from '@hub-env/environment';
import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {saveAs} from 'file-saver';
import { indexOf } from 'lodash';

@Injectable({providedIn: 'root'})
export class ApiService {
  headers: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  });

  exportHeaders: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'text/csv',
  });

  // Fixed at construction. Seam for runtime-configured origins; see environment.k8s.ts.
  apiUrl: string = environment.API_URL;

  constructor(private http: HttpClient) {}

  private getJson<T>(response: T): T {
    return response;
  }

  get<T>(path: string): Observable<T> {
    return this.http.get<T>(`${this.apiUrl}${path}`, {headers: this.headers}).pipe(map(this.getJson));
  }

  getWithLanguage<T>(path: string, languageCode: string = 'en'): Observable<T> {
    const headersLang: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Accept-Language': languageCode,
    });
    const options = {
      headers: headersLang,
    };
    return this.http.get<T>(`${this.apiUrl}${path}`, options).pipe(map(this.getJson));
  }

  getParams<T>(path: string, data: any): Observable<T> {
    let params = new HttpParams();
    for (const key in data) {
      if (key) {
        params = params.append(key, data[key]);
      }
    }
    const options = {
      params: params,
      headers: this.headers,
    };
    return this.http.get<T>(`${this.apiUrl}${path}`, options).pipe(map(this.getJson));
  }

  post<T>(path: string, body: any): Observable<T> {
    return this.http
      .post<T>(`${this.apiUrl}${path}`, JSON.stringify(body), {headers: this.headers})
      .pipe(map(this.getJson));
  }


  postExport(path: string, body: any, name: string): Observable<any> {
    return this.http
      .post(`${this.apiUrl}${path}`, JSON.stringify(body), {headers: this.exportHeaders, responseType: 'text'})
      .pipe(tap((response: string) => {
        const blob = new Blob(["\ufeff", this.formatCsvResponse(response)], {type: 'text/csv;charset=utf-16'})
        saveAs(blob, `${name}.csv`)
      }));
  }

  postForm<T>(path: string, formData: FormData): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}${path}`, formData).pipe(map(this.getJson));
  }

  put<T>(path: string, body: any): Observable<T> {
    return this.http
      .put<T>(`${this.apiUrl}${path}`, JSON.stringify(body), {headers: this.headers})
      .pipe(map(this.getJson));
  }

  putForm<T>(path: string, formData: FormData): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}${path}`, formData).pipe(map(this.getJson));
  }

  // Unlike put()/putForm(), `url` is used as-is — no apiUrl prefix, no forced JSON header. For
  // presigned URLs (e.g. S3 direct uploads) where the request must go to a foreign origin with
  // only the headers signed into the URL; AuthInterceptor already skips non-API_URL requests, so
  // no auth headers get attached either.
  putRaw<T>(url: string, body: any, headers?: HttpHeaders): Observable<T> {
    return this.http.put<T>(url, body, headers ? { headers } : {}).pipe(map(this.getJson));
  }

  patch<T>(path: string, body: any): Observable<T> {
    return this.http
      .patch<T>(`${this.apiUrl}${path}`, JSON.stringify(body), {headers: this.headers})
      .pipe(map(this.getJson));
  }

  delete<T>(path: string): Observable<T> {
    return this.http
      .delete<T>(`${this.apiUrl}${path}`, {headers: this.headers})
      .pipe(map(this.getJson));
  }

  deleteBody<T>(path: string, body: any): Observable<T> {
    return this.http
      .request<T>('delete', `${this.apiUrl}${path}`, {headers: this.headers, body: body})
      .pipe(map(this.getJson));
  }

  setHeaders(headers: any) {
    Object.keys(headers).forEach((header) => this.headers.set(header, headers[header]));
  }

  formatCsvResponse(response: string): string {
    let uppercaseHeaders = response.substring(0, response.indexOf('\n'))
    .replace(/_/g, ' ')
    .replace(/\w\S*/g, function(text) { return text.charAt(0).toUpperCase() + text.substring(1).toLowerCase() })
    .replace(/,[a-z]/g, function(text) { return text.toUpperCase() });;
    return uppercaseHeaders + response.substring(response.indexOf('\n'));
  }

  getCsv(path: string, name: string = 'export'): Observable<any> {
    return this.http
      .get(`${this.apiUrl}${path}`, {headers: this.exportHeaders, responseType: 'text'})
      .pipe(tap((response: string) => {
        const blob = new Blob(["\ufeff", this.formatCsvResponse(response)], {type: 'text/csv;charset=utf-16'});
        saveAs(blob, `${name}.csv`);
      }));
  }
}
